import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { LINE_STATUS } from "@/lib/status";
import { fmtDuree, fmtNum, fmtPct, TONE_TEXT, trsTone } from "@/lib/format";
import { TrsBar } from "@/components/common/TrsBar";
import { StatusBadge } from "@/components/common/StatusBadge";

const LiveDuration = ({ since }) => {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  return <span className="font-mono">{fmtDuree((Date.now() - new Date(since).getTime()) / 1000)}</span>;
};

export const LineCard = ({ line, corrige = false }) => {
  const s = LINE_STATUS[line.statut] || LINE_STATUS.sans_of;
  const of = line.of_actif;
  // Temps réel : on suit la production EN COURS (OF actif) dès qu'elle a des données ; le TRS du jour (tous les OF
  // de la journée mélangés) reste affiché en petit. Sans OF actif, la carte garde le TRS du jour comme avant.
  const surOf = !!of && of.trs_of != null;
  const trsPrincipal = surOf ? of.trs_of : line.trs;
  const dispo = surOf ? of.disponibilite_of : line.disponibilite;
  const perf = surOf ? of.performance_of : line.performance;
  const qual = surOf ? of.qualite_of : line.qualite;
  const tone = trsTone(trsPrincipal);
  return (
    <article data-testid={`line-card-${line.ligne_id}`} className={cn("panel relative overflow-hidden flex flex-col", line.statut === "hors_service" && "opacity-60")}>
      <span className={cn("status-stripe", s.stripe)} />
      <header className="flex items-start justify-between gap-2 pl-4 pr-3 pt-2.5 pb-2 border-b border-border/70">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate" data-testid={`line-card-${line.ligne_id}-name`}>{line.ligne}</div>
          <div className="text-[11px] text-muted-foreground truncate">{of ? <>OF <span className="font-mono text-foreground/90">{of.numero}</span> · {of.quart}{corrige && <span data-testid={`line-card-${line.ligne_id}-corrige`} title="Une saisie de cet OF a été corrigée ou annulée dans les dernières 24 h" className="ml-1.5 font-mono text-[9px] uppercase px-1 py-px border rounded-sm border-amber-500/40 text-amber-300 bg-amber-500/10">corrigé</span>}</> : "Aucun OF en cours"}</div>
        </div>
        <StatusBadge config={LINE_STATUS} code={line.statut} testId={`line-card-${line.ligne_id}-status`} />
      </header>

      <div className="pl-4 pr-3 py-2.5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-center">
        <div className="text-center">
          <div className={cn("font-mono text-3xl font-bold leading-none", TONE_TEXT[tone])} data-testid={`line-card-${line.ligne_id}-trs`}>{trsPrincipal != null ? fmtNum(trsPrincipal, 1) : "—"}</div>
          <div className="eyebrow mt-1" data-testid={`line-card-${line.ligne_id}-trs-label`}>{surOf ? "TRS OF en cours %" : "TRS jour %"}</div>
          {surOf && <div className="mt-0.5 text-[10px] font-mono text-muted-foreground" data-testid={`line-card-${line.ligne_id}-trs-jour`} title="TRS de toute la journée industrielle, tous les OF de la ligne confondus">jour : {line.trs != null ? `${fmtNum(line.trs, 1)} %` : "—"}</div>}
        </div>
        <div className="space-y-1">
          <TrsBar label="D" value={dispo} />
          <TrsBar label="P" value={perf} />
          <TrsBar label="Q" value={qual} />
        </div>
      </div>

      {line.arret_categorie && (
        <div className={cn("mx-3 mb-2 px-2 py-1.5 rounded-sm border text-[11px] flex items-center justify-between", line.arret_planifie ? "border-amber-500/40 bg-amber-950/30 text-amber-300" : "border-red-500/40 bg-red-950/30 text-red-300")}>
          <span className="font-medium truncate">{line.arret_categorie}</span>
          {line.arret_depuis && <LiveDuration since={line.arret_depuis} />}
        </div>
      )}

      {of ? (
        <div className="pl-4 pr-3 pb-2.5 space-y-1.5 text-[11px]">
          <div className="truncate text-muted-foreground" title={of.produit}>{of.produit}</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-muted rounded-sm overflow-hidden"><div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${Math.min(of.avancement_pct || 0, 100)}%` }} /></div>
            <span className="font-mono">{fmtNum(of.bonnes, of.unite_decimales)} / {fmtNum(of.cible, of.unite_decimales)} {of.unite_abr}</span>
          </div>
          <div className="grid grid-cols-3 gap-2 font-mono text-muted-foreground">
            <div><span className="eyebrow block">Cadence</span><span className={cn(of.cadence_reelle != null && of.cadence_reelle < of.cadence_cible * 0.85 ? "text-amber-400" : "text-foreground")}>{of.cadence_reelle != null ? fmtNum(of.cadence_reelle) : "—"}</span><span className="text-[10px]"> / {fmtNum(of.cadence_cible)} /h</span></div>
            <div><span className="eyebrow block">Rejets</span><span className={cn(of.rejets > 0 ? "text-amber-400" : "text-foreground")}>{fmtNum(of.rejets, of.unite_decimales)}</span></div>
            <div><span className="eyebrow block">Resp.</span><span className="text-foreground truncate block font-sans">{line.responsable || "—"}</span></div>
          </div>
        </div>
      ) : (
        <div className="pl-4 pr-3 pb-2.5 text-[11px] text-muted-foreground">{fmtNum(line.bonnes)} bonnes · {fmtNum(line.rejets)} rejets sur la journée</div>
      )}

      <footer className="mt-auto pl-4 pr-3 py-1.5 border-t border-border/60 bg-muted/20 flex items-center justify-between gap-2">
        <span className="text-[10.5px] text-muted-foreground truncate italic" title={line.diagnostic}>{line.diagnostic}</span>
        <Link to={`/trs?ligne_id=${line.ligne_id}`} className="text-[10px] font-mono uppercase text-primary hover:underline shrink-0" data-testid={`line-card-${line.ligne_id}-detail-link`}>Détail</Link>
      </footer>
      <span className="hidden">{fmtPct(0)}</span>
    </article>
  );
};
